// Ledger Report Page
// This page displays the ledger (accounting entries) for a selected customer and date range.
// It is NOT for creating ledgers (see LedgerMaster.js for that).

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TextField, Grid, ToggleButtonGroup, ToggleButton, Autocomplete
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import baseURL from '../baseURL';
import GirviPageShell from '../components/common/GirviPageShell';
import FinancialYearSelector from '../components/common/FinancialYearSelector';
import { printLedger } from '../utils/printUtility';
const API_URL = baseURL + '/accounting-entries';

/** Type-to-search: match any name/code/village token (case-insensitive). Cap list for performance. */
function filterLedgerOptions(options, inputValue, fieldsFn, limit = 150) {
  const q = String(inputValue || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!q) return options.slice(0, Math.min(50, limit));
  const tokens = q.split(' ').filter(Boolean);
  return options
    .filter((opt) => {
      const hay = fieldsFn(opt).filter(Boolean).join(' ').toLowerCase();
      return tokens.every((t) => hay.includes(t));
    })
    .slice(0, limit);
}

const Ledgers = () => {
  const [user, setUser] = useState(null);
  const [customerCode, setCustomerCode] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [from, setFrom] = useState('2025-04-01');
  const [to, setTo] = useState('2025-05-20');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** Customer book: net from accounting entries before period start (brought forward) */
  const [customerOpening, setCustomerOpening] = useState({ debit: 0, credit: 0 });
  /** Principal on old no-accounting pledges (server /pledges/customer-unposted-principal-opening) */
  const [pledgeOpeningSupplement, setPledgeOpeningSupplement] = useState(0);
  /** General ledger book: opening from Ledger Master (Debit/Credit columns) */
  const [ledgerOpening, setLedgerOpening] = useState({ debit: 0, credit: 0 });
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  /** Customer-only list for “customer” ledger book (pledges/receipts by customer code). */
  const [customers, setCustomers] = useState([]);
  /** General ledgers for “ledger account” book (Cash, Sundry Pledge, etc.) */
  const [generalLedgers, setGeneralLedgers] = useState([]);
  const [reportMode, setReportMode] = useState('customer');
  const [ledgerAccountId, setLedgerAccountId] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  };

  // Fetch user profile, customers, general ledgers, and financial years on mount
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const profileRes = await fetch(baseURL + '/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = await profileRes.json();
        setUser(profile.data?.user || null);
      } catch {
        setUser(null);
      }
      try {
        const [custRes, genRes] = await Promise.all([
          fetch(`${baseURL}/ledgers?type=Customer&limit=10000`, { headers: authHeaders() }),
          fetch(`${baseURL}/ledgers?type=General&limit=10000`, { headers: authHeaders() })
        ]);
        const custJson = await custRes.json();
        const genJson = await genRes.json();
        const custData = custJson.data || [];
        const genData = genJson.data || [];
        setCustomers(Array.isArray(custData) ? custData : []);
        setGeneralLedgers(Array.isArray(genData) ? genData : []);
      } catch {
        setCustomers([]);
        setGeneralLedgers([]);
      }
      try {
        const yearsRes = await fetch(`${baseURL}/master/financial-years?limit=10000`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const yearsData = await yearsRes.json();
        if (yearsData.success) {
          setFinancialYears(yearsData.data || []);
          // Set default to current year if available
          const currentYear = yearsData.data?.find(y => y.isCurrent);
          if (currentYear) {
            setSelectedFinancialYear(currentYear.yearName);
            const startDate = new Date(currentYear.startDate).toISOString().split('T')[0];
            const endDate = new Date(currentYear.endDate).toISOString().split('T')[0];
            setFrom(startDate);
            setTo(endDate);
          }
        }
      } catch {
        setFinancialYears([]);
      }
    };
    fetchData();
  }, []);

  // Load opening balance from Ledger Master when viewing a General ledger account
  useEffect(() => {
    if (reportMode !== 'ledgerAccount' || !ledgerAccountId) {
      setLedgerOpening({ debit: 0, credit: 0 });
      return;
    }
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseURL}/ledgers/${ledgerAccountId}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (!res.ok) return;
        const data = await res.json();
        const amt = parseFloat(data.openingBalance, 10) || 0;
        if (!amt) {
          setLedgerOpening({ debit: 0, credit: 0 });
          return;
        }
        if (data.openingBalanceType === 'Debit') setLedgerOpening({ debit: amt, credit: 0 });
        else if (data.openingBalanceType === 'Credit') setLedgerOpening({ debit: 0, credit: amt });
        else setLedgerOpening({ debit: 0, credit: 0 });
      } catch {
        setLedgerOpening({ debit: 0, credit: 0 });
      }
    };
    load();
  }, [reportMode, ledgerAccountId]);

  // Fetch ledger entries (by customer code OR by General ledger id)
  useEffect(() => {
    if (reportMode === 'customer' && !customerCode) {
      setEntries([]);
      return;
    }
    if (reportMode === 'ledgerAccount' && !ledgerAccountId) {
      setEntries([]);
      return;
    }
    const filterEntryForBook = (entry) => {
      if (entry.entryType === 'opening_balance') return false;
      if (entry.description?.toLowerCase().includes('capital freed up')) return false;
      return true;
    };

    const sumOpeningFromEntries = (list) => {
      let d = 0;
      let c = 0;
      list.filter(filterEntryForBook).forEach((e) => {
        if (e.transactionType === 'debit') d += Number(e.amount) || 0;
        else c += Number(e.amount) || 0;
      });
      const net = d - c;
      if (net >= 0) return { debit: net, credit: 0 };
      return { debit: 0, credit: -net };
    };

    const fetchEntries = async () => {
      setLoading(true);
      setError('');
      
      try {
        const token = localStorage.getItem('token');
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const params = new URLSearchParams();
        params.append('from', from);
        params.append('to', to);
        params.append('limit', '5000');
        if (reportMode === 'customer') {
          params.append('customerCode', customerCode);
        } else {
          params.append('ledgerId', ledgerAccountId);
        }
        const apiUrl = `${API_URL}?${params.toString()}`;

        let priorOpeningUrl = null;
        if (reportMode === 'customer' && customerCode && from) {
          const [y, mo, da] = from.split('-').map((n) => parseInt(n, 10));
          if (!Number.isNaN(y) && !Number.isNaN(mo) && !Number.isNaN(da)) {
            const priorDay = new Date(y, mo - 1, da - 1);
            const toPrior = `${priorDay.getFullYear()}-${String(priorDay.getMonth() + 1).padStart(2, '0')}-${String(priorDay.getDate()).padStart(2, '0')}`;
            const openParams = new URLSearchParams();
            openParams.append('customerCode', customerCode);
            openParams.append('to', toPrior);
            openParams.append('limit', '10000');
            priorOpeningUrl = `${API_URL}?${openParams.toString()}`;
          }
        }

        const requests = [fetch(apiUrl, { headers })];
        if (priorOpeningUrl) requests.push(fetch(priorOpeningUrl, { headers }));

        const responses = await Promise.all(requests);
        const res = responses[0];
        console.log('API Response Status:', res.status);

        const result = await res.json();
        const data = result.data || [];
        setEntries(data);

        let ri = 1;
        let acctOpening = { debit: 0, credit: 0 };
        if (priorOpeningUrl && responses[ri]) {
          const openJson = responses[ri].ok ? await responses[ri].json() : { data: [] };
          acctOpening = sumOpeningFromEntries(openJson.data || []);
        }
        if (reportMode === 'customer') {
          setCustomerOpening({
            debit: acctOpening.debit,
            credit: acctOpening.credit
          });
        } else {
          setCustomerOpening({ debit: 0, credit: 0 });
        }

        if (data.length > 0 && reportMode === 'customer') {
          setCustomerName(data[0].customerDetails?.name || '');
        } else if (reportMode === 'customer') {
          setCustomerName('');
        }
        
      } catch (error) {
        console.error('Error fetching ledger entries:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
        setError('Error fetching ledger entries');
      } finally {
        setLoading(false);
        console.log('=== END LEDGER SEARCH ===');
      }
    };
    fetchEntries();
  }, [reportMode, customerCode, ledgerAccountId, from, to]);

  /** Pledge principal not on books (separate request — avoids Promise.all response order bugs). */
  useEffect(() => {
    if (reportMode !== 'customer' || !customerCode || !from) {
      setPledgeOpeningSupplement(0);
      return;
    }
    const ac = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams({
          customerCode: String(customerCode).trim(),
          beforeDate: String(from).trim()
        });
        const url = `${baseURL}/pledges/customer-unposted-principal-opening?${params.toString()}`;
        const res = await fetch(url, { headers: authHeaders(), signal: ac.signal });
        const j = await res.json().catch(() => ({}));
        if (ac.signal.aborted) return;
        if (!res.ok || j.success === false) {
          console.warn('customer-unposted-principal-opening', res.status, j);
          setPledgeOpeningSupplement(0);
          return;
        }
        setPledgeOpeningSupplement(Number(j.totalPrincipal) || 0);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('customer-unposted-principal-opening', e);
          setPledgeOpeningSupplement(0);
        }
      }
    })();
    return () => ac.abort();
  }, [reportMode, customerCode, from]);

  const mergedCustomerOpening = useMemo(
    () => ({
      debit: customerOpening.debit,
      credit: customerOpening.credit + (reportMode === 'customer' ? pledgeOpeningSupplement : 0)
    }),
    [customerOpening, pledgeOpeningSupplement, reportMode]
  );

  // Totals must include Ledger Master opening when in ledger-account mode (opening loads async)
  useEffect(() => {
    const filtered = entries.filter((entry) => {
      if (entry.entryType === 'opening_balance') return false;
      if (entry.description?.toLowerCase().includes('capital freed up')) return false;
      // Release DEBIT = cash / bank received — must show on Cash ledger & full customer book (do not hide)
      return true;
    });
    let debit = 0;
    let credit = 0;
    filtered.forEach((e) => {
      if (e.transactionType === 'debit') debit += e.amount;
      else credit += e.amount;
    });
    if (reportMode === 'ledgerAccount') {
      debit += ledgerOpening.debit;
      credit += ledgerOpening.credit;
    }
    if (reportMode === 'customer') {
      debit += mergedCustomerOpening.debit;
      credit += mergedCustomerOpening.credit;
    }
    setTotals({ debit, credit });
  }, [entries, ledgerOpening, mergedCustomerOpening, reportMode]);

  // Format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const openingNet =
    reportMode === 'ledgerAccount'
      ? ledgerOpening.debit - ledgerOpening.credit
      : reportMode === 'customer'
        ? mergedCustomerOpening.debit - mergedCustomerOpening.credit
        : 0;

  // Calculate running balance (opening from master + transactions; skip posted opening_balance lines to avoid double count)
  const getRowsWithBalance = () => {
    let balance = openingNet;

    const filteredEntries = entries.filter((entry) => {
      if (entry.entryType === 'opening_balance') return false;
      if (entry.description?.toLowerCase().includes('capital freed up')) {
        return false;
      }
      return true;
    });
    
    return filteredEntries.map((entry, idx) => {
      let debit = entry.transactionType === 'debit' ? entry.amount : 0;
      let credit = entry.transactionType === 'credit' ? entry.amount : 0;
      balance += debit - credit;
      return { ...entry, debit, credit, balance: Math.abs(balance), drcr: balance >= 0 ? 'DR' : 'CR' };
    });
  };

  const handlePrint = async () => {
    if (entries.length === 0) {
      alert('Select an account and ensure there are entries to print');
      return;
    }
    if (reportMode === 'customer' && !customerCode) {
      alert('Please select a customer');
      return;
    }
    if (reportMode === 'ledgerAccount' && !ledgerAccountId) {
      alert('Please select a ledger account');
      return;
    }

    let selectedCustomer;
    if (reportMode === 'customer') {
      selectedCustomer = customers.find(c => c.code === customerCode);
      if (!selectedCustomer) {
        alert('Customer not found');
        return;
      }
    } else {
      const gl = generalLedgers.find(l => l._id === ledgerAccountId);
      if (!gl) {
        alert('Ledger not found');
        return;
      }
      selectedCustomer = {
        firstName: gl.firstName || '',
        lastName: gl.lastName || '',
        code: gl.code || '',
        fatherName: ''
      };
    }

    const filteredEntries = entries.filter(entry => {
      if (entry.entryType === 'opening_balance') return false;
      if (entry.description?.toLowerCase().includes('capital freed up')) {
        return false;
      }
      return true;
    });

    // Transform entries to match print template format
    const transactions = filteredEntries.map(entry => ({
      date: entry.date,
      ticketNo: entry.ticketNo || '',
      receiptNumber: entry.receiptNumber || entry.ticketNo || '',
      transactionType: entry.transactionType, // 'debit' or 'credit'
      amount: entry.amount,
      source: entry.source, // 'pledge', 'receipt', 'release', 'adjustment'
      description: entry.description || '',
      ...entry
    }));

    const ledgerData = {
      customer: {
        firstName: selectedCustomer.firstName || '',
        lastName: selectedCustomer.lastName || '',
        fatherName: selectedCustomer.fatherName || '',
        code: selectedCustomer.code || customerCode,
        ...selectedCustomer
      },
      transactions: transactions,
      period: {
        start: from,
        end: to
      },
      pageNumber: 1,
      openingBalance: openingNet
    };

    await printLedger(ledgerData);
  };

  return (
    <GirviPageShell
      title="Ledger"
      subtitle="Transactions · Registers"
      chipLabel="Transactions"
      maxWidth="xl"
    >
      <Paper sx={{ p: 2, boxShadow: 0, borderRadius: 2, fontFamily: 'monospace', background: '#fff', border: '1px solid #e2e8f0' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          {user && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>{user.name}</Typography>
              <Typography variant="body1">{user.companyName}</Typography>
              <Typography variant="body2">{user.address}</Typography>
              <Typography variant="body2">Mobile: {user.mobileNumber}</Typography>
              <Typography variant="body2">License: {user.licenseNumber}</Typography>
            </>
          )}
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            LEDGER FOR THE PERIOD {from} TO {to}
          </Typography>
        </Box>
        {/* Account Holder and Filters */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Report by:</Typography>
            <ToggleButtonGroup
              value={reportMode}
              exclusive
              size="small"
              onChange={(_, v) => {
                if (v) {
                  setReportMode(v);
                  setCustomerCode('');
                  setLedgerAccountId('');
                  setCustomerName('');
                }
              }}
            >
              <ToggleButton value="customer">Customer (pledges / receipts by customer)</ToggleButton>
              <ToggleButton value="ledgerAccount">Ledger account (Cash, Sundry Pledge, etc.)</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {reportMode === 'customer' && customerName
                ? `${customerName} (${customerCode})`
                : reportMode === 'ledgerAccount' && ledgerAccountId
                  ? (() => {
                      const g = generalLedgers.find(l => l._id === ledgerAccountId);
                      return g ? `${g.firstName || ''} ${g.lastName || ''} (${g.code})`.trim() : 'Select ledger account';
                    })()
                  : reportMode === 'customer'
                    ? 'Select customer'
                    : 'Select ledger account'}
            </Typography>
            {reportMode === 'customer' ? (
              <Autocomplete
                sx={{ minWidth: 600, maxWidth: 800 }}
                size="small"
                options={customers}
                value={customers.find((c) => c.code === customerCode) || null}
                onChange={(_, newValue) => {
                  setCustomerCode(newValue?.code || '');
                  setCustomerName(
                    newValue
                      ? [newValue.firstName, newValue.lastName].filter(Boolean).join(' ')
                      : ''
                  );
                }}
                isOptionEqualToValue={(a, b) => a?.code === b?.code}
                getOptionLabel={(c) =>
                  c
                    ? `${[c.firstName, c.lastName].filter(Boolean).join(' ')} (${c.code || ''})`.trim()
                    : ''
                }
                filterOptions={(opts, state) =>
                  filterLedgerOptions(opts, state.inputValue, (c) => [
                    c.firstName,
                    c.lastName,
                    c.code,
                    c.village,
                    c.atPost,
                    c.taluka,
                    c.district,
                    c.occupation,
                    c.caste,
                    c.mobileNo
                  ])
                }
                renderOption={(props, c) => (
                  <Box component="li" {...props} key={c.code || c._id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {c.firstName} {c.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        Code: {c.code} | {c.occupation || 'N/A'} | {c.caste || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        {c.village || 'N/A'}, {c.atPost || 'N/A'}, {c.taluka || 'N/A'}, {c.district || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    placeholder="Type name, code, or village…"
                  />
                )}
                noOptionsText="Type to search customers"
              />
            ) : (
              <Autocomplete
                sx={{ minWidth: 600, maxWidth: 800 }}
                size="small"
                options={generalLedgers}
                value={generalLedgers.find((g) => g._id === ledgerAccountId) || null}
                onChange={(_, newValue) => {
                  setLedgerAccountId(newValue?._id || '');
                }}
                isOptionEqualToValue={(a, b) => a?._id === b?._id}
                getOptionLabel={(g) =>
                  g
                    ? `${g.code || ''} — ${[g.firstName, g.lastName].filter(Boolean).join(' ')}`.trim()
                    : ''
                }
                filterOptions={(opts, state) =>
                  filterLedgerOptions(opts, state.inputValue, (g) => [
                    g.firstName,
                    g.lastName,
                    g.code,
                    g.groupCode
                  ])
                }
                renderOption={(props, g) => (
                  <Box component="li" {...props} key={g._id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {g.code} — {g.firstName}
                        {g.lastName ? ` ${g.lastName}` : ''}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        Group: {g.groupCode || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="General ledger account"
                    placeholder="Type name or code…"
                  />
                )}
                noOptionsText="Type to search ledgers"
              />
            )}
          </Box>
          
          {/* Financial Year and Date Filters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <FinancialYearSelector
                value={selectedFinancialYear}
                onChange={(yearName) => {
                  setSelectedFinancialYear(yearName);
                  // Find the selected year and update dates
                  const selectedYear = financialYears.find(y => y.yearName === yearName);
                  if (selectedYear) {
                    const startDate = new Date(selectedYear.startDate).toISOString().split('T')[0];
                    const endDate = new Date(selectedYear.endDate).toISOString().split('T')[0];
                    setFrom(startDate);
                    setTo(endDate);
                  }
                }}
                showLabel={true}
                fullWidth={true}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="From"
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="To"
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="contained" 
                onClick={handlePrint} 
                startIcon={<PrintIcon />}
                fullWidth
                sx={{ height: '40px' }}
              >
                Print
              </Button>
            </Grid>
          </Grid>
        </Box>
        {/* Ledger Table */}
        <Box sx={{ overflowX: 'auto', fontFamily: 'monospace' }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1000, fontFamily: 'monospace' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>DATE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>P.T.No.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>DOCUMENT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>TR</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>BANK DETAILS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>DEBIT</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>CREDIT</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>BALANCE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Opening Balance — from Ledger Master for General ledger account view */}
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>OPENING BALANCE :</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {reportMode === 'ledgerAccount' && ledgerOpening.debit > 0
                      ? `₹${ledgerOpening.debit.toFixed(2)}`
                      : reportMode === 'customer' && mergedCustomerOpening.debit > 0
                        ? `₹${mergedCustomerOpening.debit.toFixed(2)}`
                        : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {reportMode === 'ledgerAccount' && ledgerOpening.credit > 0
                      ? `₹${ledgerOpening.credit.toFixed(2)}`
                      : reportMode === 'customer' && mergedCustomerOpening.credit > 0
                        ? `₹${mergedCustomerOpening.credit.toFixed(2)}`
                        : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    ₹{Math.abs(openingNet).toFixed(2)} {openingNet >= 0 ? 'DR' : 'CR'}
                  </TableCell>
                </TableRow>
                {/* Ledger Entries */}
                {getRowsWithBalance().map((row, idx) => (
                  <TableRow key={row._id || idx}>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.ticketNo || ''}</TableCell>
                    <TableCell>
                      {row.description || ''}
                      {(row.source === 'pledge' || row.source === 'pledge_no_cash') && ' - Pledge Creation'}
                      {row.source === 'receipt' && row.description?.includes('interest') && ' - Interest Payment'}
                      {row.source === 'receipt' && !row.description?.includes('interest') && ' - Payment Received'}
                      {row.source === 'release' && ' - Pledge Released'}
                      {row.source === 'adjustment' && ' - Interest Adjustment'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        textTransform: 'uppercase', 
                        fontWeight: 'bold',
                        color: row.transactionType === 'debit' ? 'error.main' : 'success.main'
                      }}>
                        {row.transactionType || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* For interest transactions, show the corresponding entry */}
                      {row.source === 'receipt' && row.description?.includes('interest') && (
                        <Typography variant="caption" color="textSecondary">
                          Interest: {row.transactionType === 'credit' ? 'Received' : 'Charged'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {row.debit ? `₹${row.debit.toFixed(2)}` : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {row.credit ? `₹${row.credit.toFixed(2)}` : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      ₹{row.balance.toFixed(2)} {row.drcr}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals */}
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.debit.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.credit.toFixed(2)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        {loading && <Typography sx={{ mt: 2 }}>Loading...</Typography>}
        <Box sx={{ mt: 2, fontStyle: 'italic', color: 'gray' }}>
          <Typography variant="caption">
            Pledge, receipt, and release lines for this customer appear here (cash, bank/UPI, sundry pledge, interest).
            After full release, principal and interest should net to zero on this book.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            Interest receipts show cash/bank debit and interest credit. Separate “interest charged” accrual lines are not posted yet.
          </Typography>
          {reportMode === 'ledgerAccount' && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'primary.main' }}>
              Cash &amp; bank (assets): enter opening as <strong>Debit</strong> for cash in hand. Credit opening reduces the balance. P&amp;L only shows income/expense ledgers — not cash.
            </Typography>
          )}
        </Box>
      </Paper>
    </GirviPageShell>
  );
};

export default Ledgers; 